import type {RefObject} from 'react'
import {useEffect, useRef} from 'react'

/**
 * Sets an explicit pixel height on the element so it fills the available
 * space inside a Primer Dialog without overflowing.
 *
 * Primer's Dialog body gets its height from flex layout, which breaks CSS
 * percentage-height resolution for children.  Instead of observing the
 * direct parent (whose `clientHeight` is content-driven and would create a
 * feedback loop), we observe the **dialog element** itself — its height is
 * fixed by CSS (`max-height: calc(100dvh - 64px)`) and independent of our
 * content.  We then compute the available space as the distance from our
 * element's top edge to the dialog's bottom edge.
 */
export function useDialogFillHeight(ref: RefObject<HTMLElement | null>) {
  const rafRef = useRef(0)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const dialog = el.closest<HTMLElement>('[role="dialog"]')
    if (!dialog) return

    function measure() {
      rafRef.current = 0
      if (!el || !dialog) return
      const available = Math.floor(dialog.getBoundingClientRect().bottom - el.getBoundingClientRect().top)
      if (available > 0 && el.style.height !== `${available}px`) {
        el.style.height = `${available}px`
      }
    }

    const ro = new ResizeObserver(() => {
      if (rafRef.current) return
      rafRef.current = requestAnimationFrame(measure)
    })

    measure()
    ro.observe(dialog)

    return () => {
      ro.disconnect()
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      el.style.height = ''
    }
  }, [ref])
}
